/**
 * Pure bits of the AI New Site wizard (AB#642) — the first message Baxter gets,
 * and which model the wizard opens on.
 */

import type { AiModel } from '../api/aiModels';


/** The free router — same `provider:model` value the Baxter picker stores. */
export const FREE_MODEL = 'openrouter:openrouter/free';

/** Rick's ranking: a strong model is preferred, so a key for one of these wins. */
const PREFERRED_PROVIDERS = ['anthropic', 'openai', 'openrouter'];

/** The picker's value for a model row (same shape as BaxterPanel's `modelValue`). */
export const modelValue = (m: AiModel) => `${m.provider}:${m.id}`;

/** Remove only a repeated vendor prefix; keep model/version/context qualifiers intact. */
export const modelDisplayName = (m: Pick<AiModel, 'name'>) => m.name.replace(/^([a-z][a-z0-9 ._-]*):\s*(?=\1(?:\s|[-/]))/i, '');

/** Provider half of a `provider:model` value. */
export function modelProvider(value: string): string {
  const sep = value.indexOf(':');
  return sep > 0 ? value.slice(0, sep) : value;
}

/** Reuse catalog capability ratings; ties stay stable except Rick's named Astra preference. */
export function orderedModels(models: AiModel[]): AiModel[] {
  return [...models].sort((a, b) => (b.strength ?? -1) - (a.strength ?? -1)
    || Number(b.id.split('/').at(-1) === 'gpt-6-astra') - Number(a.id.split('/').at(-1) === 'gpt-6-astra'));
}

/**
 * Default model: the first non-free model from the strongest provider the
 * person actually has a key for; failing that any keyed provider, then a paid
 * OpenRouter model, then the free router (which the UI flags as rough).
 */
export function pickDefaultModel(models: AiModel[], keyedProviders: string[]): string {
  const paid = orderedModels(models).filter((m) => !m.free);
  for (const provider of PREFERRED_PROVIDERS) {
    if (!keyedProviders.includes(provider)) continue;
    const hit = paid.find((m) => m.provider === provider);
    if (hit) return modelValue(hit);
  }
  const anyKeyed = paid.find((m) => keyedProviders.includes(m.provider));
  if (anyKeyed) return modelValue(anyKeyed);
  const openrouter = paid.find((m) => m.provider === 'openrouter');
  return openrouter ? modelValue(openrouter) : FREE_MODEL;
}

/** The wizard's first user turn, handed to Baxter through `baxterBrief`. */
export function buildBrief({
  name,
  description,
  media,
  url,
}: {
  name: string;
  description: string;
  media: Array<{ filename: string; url: string }>;
  /** Port mode: the preview link Baxter rebuilds here. */
  url?: string;
}): string {
  const parts = [
    url?.trim()
      ? `Bring over the site at ${url.trim()} — rebuild it here as "${name.trim()}" with its pages, copy, colours and a matching theme.`
      : `Build my site "${name.trim()}".`,
  ];
  const desc = description.trim();
  if (desc) parts.push(desc);
  if (media.length) {
    const list = media.map((m) => `${m.filename} (${m.url})`).join(', ');
    parts.push(`I uploaded these files (already in the media library): ${list}.`);
  }
  return parts.join('\n\n');
}
